import { flexRender, getCoreRowModel, getExpandedRowModel, getPaginationRowModel, getSortedRowModel, useReactTable } from "@tanstack/react-table";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dropdown, Icon } from "semantic-ui-react";

import { Pagination, TableOverlayLoader } from "components";
import "components/ReactTable/ReactTable.scss";
import { PAGE_SIZE } from "utils/common";

import type {
  AccessorColumnDef,
  AccessorKeyColumnDef,
  Column,
  ExpandedState,
  Header,
  OnChangeFn,
  PaginationState,
  Row,
  SortingState,
  VisibilityState
} from "@tanstack/react-table";
import type { ReactNode } from "react";

export type HeaderType<T> = {
  column: Column<T, unknown>;
  isPlaceholder: boolean;
  sortingState?: { id: string; desc: boolean };
  renderHeader: () => ReactNode;
  toggleSorting?: (event?: unknown) => void;
};

type ReactTableProps<T> = {
  data?: T[] | null;
  columns: AccessorColumnDef<T, any>[];
  emptyRowMessage?: string;
  state?: { columnVisibility: Record<string, boolean> };
  pageSize?: number;
  pageCount?: number;
  totalItems?: number;
  enableSorting?: boolean;
  pagination?: PaginationState;
  columnSortingEnabled?: boolean;
  overrideTableBodyMessage?: string | null;
  onRowClick?: (d: any) => void;
  onChangePageSize?: (value: number) => void;
  onPaginationChange?: OnChangeFn<PaginationState>;
  renderExpandedComponent?: (row: Row<T>) => ReactNode;
  customHeader?: (props: { headers: HeaderType<T>[] }) => ReactNode;
  expanded?: ExpandedState;
  onExpandedChange?: any;
  getRowId?: (row: T) => string;
  isFetching?: boolean;
};

const pageSizeOptions = [
  { key: PAGE_SIZE.PageSize25, text: `${PAGE_SIZE.PageSize25}`, value: PAGE_SIZE.PageSize25 },
  { key: PAGE_SIZE.PageSize50, text: `${PAGE_SIZE.PageSize50}`, value: PAGE_SIZE.PageSize50 },
  { key: PAGE_SIZE.PageSize100, text: `${PAGE_SIZE.PageSize100}`, value: PAGE_SIZE.PageSize100 }
];

const isAccessorKeyColumn = <T,>(column: AccessorColumnDef<T>): column is AccessorKeyColumnDef<T> => "accessorKey" in column;

const InternalReactTable = <T,>({
  data,
  columns,
  customHeader,
  emptyRowMessage = "",
  state,
  onRowClick,
  pageCount,
  totalItems,
  renderExpandedComponent,
  pagination,
  pageSize,
  columnSortingEnabled,
  onPaginationChange,
  overrideTableBodyMessage,
  enableSorting = false,
  onChangePageSize,
  expanded,
  onExpandedChange,
  getRowId
}: ReactTableProps<T>) => {
  const { t } = useTranslation();

  const [sorting, setSorting] = useState<SortingState>(() =>
    columns.map(c => (isAccessorKeyColumn(c) ? { id: c.accessorKey.toString(), desc: false } : { id: c.id ?? "", desc: false }))
  );
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});

  const mergedColumnVisibility = { ...columnVisibility, ...state?.columnVisibility };

  const tableData = useMemo(() => data ?? [], [data]);

  const table = useReactTable({
    data: tableData,
    columns,
    getRowCanExpand: () => true,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    enableSortingRemoval: false,
    onSortingChange: setSorting,
    enableSorting,
    onColumnVisibilityChange: setColumnVisibility,
    ...(pagination
      ? {
          getPaginationRowModel: getPaginationRowModel(),
          state: {
            ...(state || { sorting }),
            columnVisibility: mergedColumnVisibility,
            pagination,
            expanded
          },
          onPaginationChange,
          manualPagination: true,
          pageCount
        }
      : {
          state: { ...state, columnVisibility: mergedColumnVisibility, sorting, expanded }
        }),
    onExpandedChange,
    getRowId
  });

  const getColumnSortingState = (columnId: string) => sorting.find(s => s.id === columnId);

  const renderCustomHeader = (header: Header<T, unknown>) => flexRender(header.column.columnDef.header, header.getContext());

  const renderTableBody = () => {
    if (overrideTableBodyMessage)
      return (
        <tr>
          <td colSpan={columns.length} className="no-results">
            <span> {overrideTableBodyMessage} </span>
          </td>
        </tr>
      );

    if (table.getRowModel().rows.length > 0)
      return table.getRowModel().rows.map(row => {
        const expandedContent = renderExpandedComponent && row.getIsExpanded() ? renderExpandedComponent(row) : null;

        return (
          <React.Fragment key={row.id}>
            <tr key={row.id} onClick={() => onRowClick?.(row.original)} data-state={row.getIsSelected() && "selected"}>
              {row.getVisibleCells().map(cell => (
                <td key={cell.id} className={`td-${cell.column.id}`} style={{ width: cell.column.columnDef.size }}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>

            {expandedContent && (
              <tr key={`${row.id}-expanded`} className="expanded-row-content">
                <td colSpan={columns.length}>
                  <div className="expanded">{expandedContent}</div>
                </td>
              </tr>
            )}
          </React.Fragment>
        );
      });

    if (emptyRowMessage)
      return (
        <tr>
          <td colSpan={columns.length} className="text-center">
            {emptyRowMessage}
          </td>
        </tr>
      );

    return (
      <tr>
        <td colSpan={columns.length} className="no-results">
          <span> {t("v8_no_results", "No results")}. </span>
        </td>
      </tr>
    );
  };

  return (
    <div className="ReactTable-wrapper">
      <div className="ReactTable-body">
        <table className="ReactTable">
          <thead>
            {customHeader
              ? customHeader({
                  headers: table.getHeaderGroups().flatMap(headerGroup =>
                    headerGroup.headers.map(header => ({
                      column: header.column,
                      isPlaceholder: header.isPlaceholder,
                      sortingState: getColumnSortingState(header.column.id),
                      toggleSorting: header.column.getToggleSortingHandler(),
                      renderHeader: () => renderCustomHeader(header)
                    }))
                  )
                })
              : table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => {
                      const columnSortingState = getColumnSortingState(header.column.id);

                      return (
                        <th style={{ width: header.column.columnDef.size }} className={`th-${header.column.id}`} key={header.id}>
                          {header.isPlaceholder ? null : (
                            <>
                              {columnSortingEnabled ? (
                                <div className="title-sorting-icons-header">
                                  <div className="title">{flexRender(header.column.columnDef.header, header.getContext())}</div>

                                  <div className="icons">
                                    {columnSortingState?.desc ? (
                                      <Icon className="caret down anchor" onClick={header.column.getToggleSortingHandler()} />
                                    ) : (
                                      <Icon className="caret up anchor" onClick={header.column.getToggleSortingHandler()} />
                                    )}
                                  </div>
                                </div>
                              ) : (
                                flexRender(header.column.columnDef.header, header.getContext())
                              )}
                            </>
                          )}
                        </th>
                      );
                    })}
                  </tr>
                ))}
          </thead>
          <tbody>{renderTableBody()}</tbody>
        </table>
      </div>

      {pagination && (
        <div className="ReactTablePagination">
          {pageSize && onChangePageSize && (
            <div className="ReactTablePagSize">
              <span>{t("v8_rows_per_page", "Rows per page")} </span>
              <Dropdown
                name="page-size-selection"
                compact
                selection
                value={pageSize}
                selectOnBlur={false}
                options={pageSizeOptions}
                onChange={(_e, { value }) => onChangePageSize(Number(value))}
              />
            </div>
          )}

          <Pagination
            currentPage={table.getState().pagination.pageIndex + 1}
            totalPages={table.getPageCount()}
            onPageChange={page => {
              table.setPageIndex(page - 1);
            }}
            pageSize={pageSize || 0}
            totalItems={totalItems || 0}
          />
        </div>
      )}
    </div>
  );
};

export const ReactTable = <T,>({ isFetching, columns, pageSize, ...rest }: ReactTableProps<T>) => {
  if (isFetching) return <TableOverlayLoader columnSize={columns.length} pageSize={pageSize ?? PAGE_SIZE.PageSize25} />;

  return <InternalReactTable columns={columns} pageSize={pageSize} {...rest} />;
};
